import React, { useCallback, useState, useRef } from 'react'
import { useMessageInputContext, useChannelStateContext, useChatContext } from 'stream-chat-react'
import type { MessageResponse, Attachment } from 'stream-chat'
// import ReactDOM from 'react-dom'

import { RichTextInput } from './RichTextInput'
import { LightningBoltSmall } from '../../assets'
import type { StreamChatGenerics } from '../../types'
import { useGiphyContext } from '../../Giphy'
// import { isAdvisorRole } from 'utils/helper'
import { ReactComponent as MicIcon } from 'assets/svgs/microphone-icon.svg'
import { VoiceRecorder } from '../VoiceRecorder'

// const Portal = ({ children }) => {
//   if (typeof window === 'undefined') return null
//   return ReactDOM.createPortal(children, document.body)
// }

interface RichTextMessagingInputProps {
  showChannelCreate?: boolean
  initialContent?: string
  onSubmitOverride?: (richTextContent: string) => Promise<void> | void
}

const RichTextMessagingInput = ({ showChannelCreate = false, initialContent, onSubmitOverride }: RichTextMessagingInputProps) => {
  const { giphyState, setGiphyState } = useGiphyContext()
  const messageInput = useMessageInputContext<StreamChatGenerics>()
  const { channel } = useChannelStateContext<StreamChatGenerics>()
  const { client } = useChatContext<StreamChatGenerics>()
  
  const [quotedMessage, setQuotedMessage] = useState<MessageResponse<StreamChatGenerics> | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState<string>('')
  const [voiceDurationSeconds, setVoiceDurationSeconds] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Debug logging
 



  // const isAdvisor = isAdvisorRole()

  // Determine placeholder text based on chat type
  const otherMembers = Object.values(channel.state.members || {}).filter(
    (member) => member.user?.id !== client.user?.id
  )

  const getPlaceholderText = () => {
    if (showChannelCreate) {
      return 'Start a conversation...'
    }

    if (otherMembers.length === 1) {
      const member = otherMembers[0]
      return `Message ${member.user?.name || member.user?.id || 'user'}...`
    }

    if (otherMembers.length > 1) {
      return `Message ${otherMembers.length} people...`
    }

    return 'Type a message...'
  }

  const handleSubmit = useCallback(
    async (richTextContent: string) => {
      // If a custom submit handler is provided (e.g., for editing), use it
      if (typeof onSubmitOverride === 'function') {
        try {
          await onSubmitOverride(richTextContent)
          // Clear local state after successful submit
          messageInput.setText?.('')
          setTranscript('')
          setVoiceDurationSeconds(null)
          // Clear uploads if any
          Object.keys(messageInput.fileUploads || {}).forEach((uploadId) => {
            messageInput.removeFile?.(uploadId)
          })
          Object.keys(messageInput.imageUploads || {}).forEach((uploadId) => {
            messageInput.removeImage?.(uploadId)
          })
          messageInput.clearEditingState?.()
          return
        } catch (error) {
          console.error('Error in override submit:', error)
          return
        }
      }
      // If we have a transcript, send message with custom data directly
      if ((transcript || voiceDurationSeconds !== null) && channel) {
        try {
          // Convert file uploads to attachments format
          const attachments: Attachment<StreamChatGenerics>[] = []
          
          // Add file uploads
          Object.values(messageInput.fileUploads || {}).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'file',
                asset_url: upload.url,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                title: upload.file.name,
              })
            }
          })
          
          // Add image uploads
          Object.values(messageInput.imageUploads || {}).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'image',
                image_url: upload.url,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                title: upload.file.name,
              })
            }
          })
          
          const messageData = {
            text: richTextContent, // Use the rich text content
            attachments: attachments,
            // If replying in a thread, ensure parent_id is set so it appears under the thread
            ...(messageInput.parent?.id
              ? { parent_id: messageInput.parent.id, show_in_channel: false }
              : {}),
            custom: {
              transcript: transcript,
              voiceDurationSeconds: voiceDurationSeconds,
              richText: true // Flag to indicate this is rich text
            }
          }
          
          await channel.sendMessage(messageData)
          
          // Clear the input and transcript after successful send
          messageInput.setText('')
          setTranscript('')
          setVoiceDurationSeconds(null)
          
          // Clear all uploaded files
          Object.keys(messageInput.fileUploads || {}).forEach((uploadId) => {
            if (messageInput.removeFile) {
              messageInput.removeFile(uploadId)
            }
          })
          
          Object.keys(messageInput.imageUploads || {}).forEach((uploadId) => {
            if (messageInput.removeImage) {
              messageInput.removeImage(uploadId)
            }
          })
          
          // Clear any other editing state
          if (messageInput.clearEditingState) {
            messageInput.clearEditingState()
          }
          
        } catch (error) {
          console.error('Error sending message:', error)
        }
      } else if (channel) {
        // Send a normal rich text message directly via Stream channel
        try {
          await channel.sendMessage({
            text: richTextContent || '',
            // If replying in a thread, ensure parent_id is set so it appears under the thread
            ...(messageInput.parent?.id
              ? { parent_id: messageInput.parent.id, show_in_channel: false }
              : {}),
            custom: { richText: true },
          })
        } catch (err) {
          console.error('Error sending message:', err)
        }
      }
    },
    [messageInput, channel, transcript, voiceDurationSeconds, onSubmitOverride]
  )

  const handleTextChange = useCallback((text: string) => {
    // Avoid calling Stream's setText; not all contexts provide it.
    // If needed, we can mirror the text in local state or ignore.
  }, [])

  const handleFileUpload = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // no separate upload helper; call uploadNewFiles directly where needed

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      messageInput.uploadNewFiles?.(Array.from(files))
    }
  }, [messageInput])

  // Handle voice message
  const handleVoiceMessage = useCallback(async (audioBlob: Blob, transcript?: string, durationSeconds?: number) => {
    try {

      // Create a File object from the Blob
      const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, {
        type: 'audio/webm;codecs=opus',
      })

      // Upload the voice file using the existing file upload mechanism
      messageInput.uploadNewFiles?.([audioFile])
      
      // Store transcript and duration in state
      if (transcript) setTranscript(transcript)
      if (typeof durationSeconds === 'number') setVoiceDurationSeconds(durationSeconds)
      
      // Voice message processed successfully - hide recorder
      setIsRecording(false)
    } catch (error) {
      console.error('Error processing voice message:', error)
      setIsRecording(false)
    }
  }, [messageInput])

  // Handle voice recorder cancel
  const handleVoiceCancel = useCallback(() => {
    setIsRecording(false)
  }, [])

  // Start recording
  const startVoiceRecording = useCallback(() => {
    setIsRecording(true)
  }, [])


  return (
    <div className="messaging-input-container mx-4">
      {/* File input (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
      />

      {/* Quoted message display */}
      {quotedMessage && (
        <div className="mb-2 p-3 bg-gray-50 border-l-4 border-app-primary-60 rounded">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Replying to {quotedMessage.user?.name || 'Unknown'}</span>
              <p className="mt-1 text-gray-500 line-clamp-2">
                {(quotedMessage.text ?? '').substring(0, 100)}
                {(quotedMessage.text?.length ?? 0) > 100 ? '...' : ''}
              </p>
            </div>
            <button
              onClick={() => setQuotedMessage(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Voice recorder - show above input when active */}
      {isRecording && (
        <VoiceRecorder 
          onVoiceMessage={handleVoiceMessage}
          onCancel={handleVoiceCancel}
          className="mb-3"
          autoStart={true}
        />
      )}

      {/* Rich text input */}
      <RichTextInput
        placeholder={getPlaceholderText()}
        onSubmit={handleSubmit}
        onTextChange={handleTextChange}
        disabled={giphyState}
        onAttachClick={handleFileUpload}
        initialContent={initialContent || ''}
        rightActions={(
          <>
            {/* Mic button to match design */}
            {!isRecording && (
              <button
                type="button"
                onClick={startVoiceRecording}
                className="w-9 h-9 rounded-lg text-app-grey-60 hover:text-app-black-80 hover:bg-app-grey-20 flex items-center justify-center transition-colors"
                title="Record voice message"
              >
                <MicIcon className="w-5 h-5" />
              </button>
            )}
          </>
        )}
      />


      {/* Action buttons */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          {/* Secondary actions kept minimal; attach moved into input toolbar */}


          {/* Giphy button */}
          <button
            type="button"
            onClick={() => setGiphyState(!giphyState)}
            className={`p-2 rounded-lg transition-colors ${
              giphyState
                ? 'text-app-primary-60 bg-app-primary-10'
                : 'text-app-grey-60 hover:text-app-black-80 hover:bg-app-grey-20'
            }`}
            title="GIF"
          >
            <LightningBoltSmall />
          </button>
        </div>

        {/* File upload indicators */}
        {((messageInput.fileUploads && Object.keys(messageInput.fileUploads).length > 0) || 
          (messageInput.imageUploads && Object.keys(messageInput.imageUploads).length > 0)) && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              {Object.keys(messageInput.fileUploads || {}).length + Object.keys(messageInput.imageUploads || {}).length} file(s) attached
            </span>
            <button
              type="button"
              onClick={() => {
                // Clear all uploaded files
                Object.keys(messageInput.fileUploads || {}).forEach((uploadId) => {
                  if (messageInput.removeFile) {
                    messageInput.removeFile(uploadId)
                  }
                })
                
                Object.keys(messageInput.imageUploads || {}).forEach((uploadId) => {
                  if (messageInput.removeImage) {
                    messageInput.removeImage(uploadId)
                  }
                })
              }}
              className="text-red-500 hover:text-red-700"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Giphy search (when active) */}
      {giphyState && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Search for GIFs:</p>
          <input
            type="text"
            placeholder="Search GIFs..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-app-primary-60"
            onChange={(e) => {
              // Handle giphy search
              const query = e.target.value
              if (query.length > 2) {
                // Trigger giphy search
                messageInput.setText(`/giphy ${query}`)
              }
            }}
          />
        </div>
      )}
    </div>
  )
}

export default RichTextMessagingInput
